import React, { useEffect, useRef } from "react";
import { AlertTriangle, Loader2, Trash2, X } from "lucide-react";

/*
 * The confirmation shown before anything is removed.
 *
 * The browser's own confirm() was doing this job: it cannot say what will be
 * removed beyond one line of text, cannot be read while the page is visible
 * behind it, looks nothing like the rest of the portal, and gives no feedback
 * while the request runs. Deleting a competition removes a meeting's entire
 * results, so the dialog needs to name every item, spell out the consequence,
 * and stay put until the work has actually finished.
 *
 * `items` is the list being removed. Showing it matters most for a bulk action,
 * where "delete 12 shooters" is otherwise a leap of faith.
 */
export default function ConfirmDialog({
  open,
  title,
  intro,
  items = [],
  note,
  confirmLabel = "Delete",
  busy = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  /** One sentence naming what is about to happen. */
  intro: React.ReactNode;
  /** The things being removed, listed so a bulk action is never taken blind. */
  items?: string[];
  /** What follows from it — cascades, what is kept, whether it can be undone. */
  note?: React.ReactNode;
  confirmLabel?: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  /*
   * Escape closes and the confirm button takes focus, so the dialog is operable
   * from the keyboard the way the native one was. Focus goes to Confirm rather
   * than Cancel because the user has already chosen to delete; Escape is the
   * escape route.
   */
  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-950/60 p-4 backdrop-blur-sm"
      onClick={() => !busy && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-modal ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3.5 px-6 pb-4 pt-6">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-danger-soft text-danger">
            <AlertTriangle size={21} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-[17px] font-bold tracking-tight text-ink">{title}</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-slate-600">{intro}</p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="-mr-1.5 -mt-1.5 rounded-lg p-1.5 text-muted transition-colors hover:bg-slate-100 hover:text-ink disabled:opacity-40"
            aria-label="Close"
          >
            <X size={17} />
          </button>
        </div>

        {items.length > 0 && (
          <div className="mx-6 mb-4 rounded-xl border border-line bg-slate-50/80">
            <div className="border-b border-line px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider text-muted">
              {items.length} {items.length === 1 ? "item" : "items"}
            </div>
            {/* Bounded: a bulk delete of fifty rows must not push the buttons off screen. */}
            <ul className="slim-scrollbar max-h-40 overflow-y-auto px-3.5 py-2 text-[13px] text-ink">
              {items.map((label) => (
                <li key={label} className="flex items-start gap-2 py-0.5">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-slate-400" />
                  <span className="min-w-0 break-words font-semibold">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {note && <div className="mx-6 mb-5 rounded-xl bg-warning-soft px-3.5 py-2.5 text-[13px] leading-relaxed text-slate-700">{note}</div>}

        <div className="flex justify-end gap-2 border-t border-line bg-slate-50/60 px-6 py-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="inline-flex h-10 items-center rounded-lg border border-line bg-white px-4 text-[13px] font-semibold text-ink transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex h-10 items-center gap-2 rounded-lg bg-danger px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
            {busy ? "Removing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
